import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  BookOpen, LogOut, Menu as MenuIcon, X, Home as HomeIcon, 
  GraduationCap, ChevronRight, Instagram, Linkedin, Mail, 
  ShoppingBag, Briefcase, Music, Shirt, Shield, Handshake, 
  School, LogIn 
} from 'lucide-react';

import { supabase } from './supabaseClient'; 
import { Analytics } from "@vercel/analytics/react";

// --- IMPORT PAGINE ---
import Auth from './pages/Auth';
import ForgotPassword from './pages/ForgotPassword';
import UpdatePassword from './pages/UpdatePassword';
import Home from './pages/Home'; 
import Profile from './pages/Profile'; 
import Departments from './pages/Departments';         
import DepartmentDetails from './pages/DepartmentDetails'; 
import CourseDetails from './pages/CourseDetails';     
import Subject from './pages/Subject'; 
import NoteDetails from './pages/NoteDetails'; 
import Housing from './pages/Housing';
import Partners from './pages/Partners'; 
import Tutoring from './pages/Tutoring';
import Marketplace from './pages/Marketplace';
import Jobs from './pages/Jobs';
import Events from './pages/Events';
import Merch from './pages/Merch';
import Contact from './pages/Contact';
import AdminDashboard from './pages/AdminDashboard';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';

// Import Componenti
import StudentRoute from './components/StudentRoute';
import CookieBanner from './components/CookieBanner';
import AutoLogout from './components/AutoLogout';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// --- BOTTOM NAV (MOBILE) ---
const MobileBottomNav = ({ user, onToggleMenu, isMenuOpen }) => {
  const location = useLocation();
  const isActive = (path) => location.pathname === path;
  
  return (
    <div className="md:hidden fixed bottom-0 left-0 w-full bg-white/95 backdrop-blur-md border-t border-slate-200 z-[60] px-2 py-2 flex justify-between items-end shadow-[0_-4px_20px_rgba(0,0,0,0.05)] pb-safe">
      <Link to="/" className={`flex-1 flex flex-col items-center gap-1 p-2 transition ${isActive('/') ? 'text-indigo-600' : 'text-slate-400'}`}>
        <HomeIcon size={24} strokeWidth={isActive('/') ? 2.5 : 2} />
        <span className="text-[10px] font-bold">Home</span>
      </Link>
      <Link to="/dipartimenti" className={`flex-1 flex flex-col items-center gap-1 p-2 transition ${isActive('/dipartimenti') ? 'text-indigo-600' : 'text-slate-400'}`}>
        <GraduationCap size={24} strokeWidth={isActive('/dipartimenti') ? 2.5 : 2} />
        <span className="text-[10px] font-bold">Corsi</span>
      </Link>
      
      <div className="relative -top-5 flex-1 flex justify-center">
        <button onClick={onToggleMenu} className={`flex flex-col items-center justify-center w-14 h-14 rounded-full shadow-lg border-4 border-white transition-all ${isMenuOpen ? 'bg-indigo-700 rotate-90' : 'bg-indigo-600 hover:scale-105'}`}>
            {isMenuOpen ? <X className="text-white" size={28}/> : <MenuIcon className="text-white" size={28}/>}
        </button>
      </div>
      
      <Link to="/mercatino" className={`flex-1 flex flex-col items-center gap-1 p-2 transition ${isActive('/mercatino') ? 'text-indigo-600' : 'text-slate-400'}`}>
        <ShoppingBag size={24} strokeWidth={isActive('/mercatino') ? 2.5 : 2} />
        <span className="text-[10px] font-bold">Libri</span>
      </Link>
      <Link to="/partner" className={`flex-1 flex flex-col items-center gap-1 p-2 transition ${isActive('/partner') ? 'text-indigo-600' : 'text-slate-400'}`}>
        <Handshake size={24} strokeWidth={isActive('/partner') ? 2.5 : 2} />
        <span className="text-[10px] font-bold">Sconti</span>
      </Link>
    </div>
  );
};

// --- NAVBAR (DESKTOP) ---
const Navbar = ({ user, username, isAdmin, handleLogout }) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => { const handleScroll = () => setScrolled(window.scrollY > 10); window.addEventListener('scroll', handleScroll); return () => window.removeEventListener('scroll', handleScroll); }, []);
  const menuItems = [{ label: 'Corsi', path: '/dipartimenti' }, { label: 'Libri', path: '/mercatino' }, { label: 'Case', path: '/affitti' }, { label: 'Lavoro', path: '/jobs' }, { label: 'Eventi', path: '/eventi' }, { label: 'Partner', path: '/partner' }];

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-xl border-b border-slate-200 py-2 shadow-sm' : 'bg-white border-b border-slate-100 py-3'}`}>
      <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-3 group z-50">
            <div className="p-1.5 rounded-full bg-white border border-slate-100 shadow-sm group-hover:scale-110 transition-transform">
                <img src="/logo-th.jpg" alt="Logo" className="h-8 w-8 object-contain rounded-full"/>
            </div>
            <span className="font-bold text-xl tracking-wide text-slate-900">Tuscia<span className="text-indigo-600">Hub</span></span>
        </Link>
        <div className="hidden md:flex items-center gap-1 bg-slate-100/50 p-1 rounded-full border border-slate-200/50">
            {menuItems.map(link => (<Link key={link.path} to={link.path} className="px-4 py-2 rounded-full text-sm font-bold text-slate-500 hover:text-indigo-600 hover:bg-white transition-all shadow-sm hover:shadow-md">{link.label}</Link>))}
            {isAdmin && (<Link to="/admin" className="px-4 py-2 rounded-full text-sm font-bold text-red-500 hover:bg-red-50 transition-all flex items-center gap-1"><Shield size={14} /> Admin</Link>)}
        </div>
        <div className="flex items-center gap-4 z-50">
            {user ? (
                <div className="flex items-center gap-3 pl-4 md:border-l border-slate-200">
                    <Link to="/profile" className="text-sm font-bold text-slate-700 truncate max-w-[150px] hidden md:block hover:text-indigo-600 transition">{username}</Link>
                    <button onClick={handleLogout} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all bg-white md:bg-transparent shadow-sm md:shadow-none border md:border-none border-slate-100"><LogOut size={20} /></button>
                </div>
            ) : (
                <Link to="/login" className="bg-indigo-600 text-white font-bold py-2 px-4 rounded-full hover:bg-indigo-700 transition-all shadow-lg hover:-translate-y-0.5 text-xs md:text-sm flex items-center gap-2"><LogIn size={16} /> <span>Accedi</span></Link>
            )}
        </div>
      </div>
    </nav>
  );
};

// --- MOBILE MENU ---
const MobileMenuOverlay = ({ isOpen, onClose, user, username, isAdmin, handleLogout }) => {
  if (!isOpen) return null;
  const links = [{ label: 'Corsi & Dispense', path: '/dipartimenti', icon: GraduationCap, color: 'text-blue-600', bg: 'bg-blue-50' }, { label: 'Mercatino Libri', path: '/mercatino', icon: BookOpen, color: 'text-orange-500', bg: 'bg-orange-50' }, { label: 'Cerca Casa', path: '/affitti', icon: HomeIcon, color: 'text-green-600', bg: 'bg-green-50' }, { label: 'Ripetizioni', path: '/ripetizioni', icon: School, color: 'text-purple-600', bg: 'bg-purple-50' }, { label: 'Lavoro & Stage', path: '/jobs', icon: Briefcase, color: 'text-sky-600', bg: 'bg-sky-50' }, { label: 'Eventi', path: '/eventi', icon: Music, color: 'text-pink-600', bg: 'bg-pink-50' }, { label: 'Sconti', path: '/partner', icon: Handshake, color: 'text-rose-600', bg: 'bg-rose-50' }, { label: 'Merch', path: '/merch', icon: Shirt, color: 'text-yellow-500', bg: 'bg-yellow-50' }];
  
  return (
    <div className="fixed inset-0 bg-white z-[55] pt-24 pb-24 px-6 overflow-y-auto animate-in slide-in-from-bottom duration-300 md:hidden flex flex-col">
        <div className="grid grid-cols-2 gap-4 mb-8">
            {links.map(link => (
                <Link key={link.path} to={link.path} onClick={onClose} className="flex flex-col items-center justify-center p-5 rounded-3xl border border-slate-100 shadow-sm hover:shadow-md transition-all bg-slate-50/30 active:scale-95">
                    <div className={`mb-3 p-3 rounded-2xl ${link.bg} ${link.color} shadow-inner`}><link.icon size={26} /></div>
                    <span className="font-bold text-slate-800 text-xs text-center">{link.label}</span>
                </Link>
            ))}
        </div>
        <div className="mt-auto">
            {user ? (
                <div className="bg-slate-50 p-5 rounded-3xl flex items-center justify-between border border-slate-200">
                    <Link to="/profile" onClick={onClose} className="flex items-center gap-3 overflow-hidden">
                        <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-lg shrink-0">{username.charAt(0).toUpperCase()}</div>
                        <div className="min-w-0">
                            <p className="font-bold text-slate-900 truncate">{username}</p>
                            <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Vai al Profilo</p>
                        </div>
                    </Link>
                    <button onClick={() => {handleLogout(); onClose();}} className="p-2.5 bg-white border border-slate-200 rounded-xl text-red-500 shadow-sm"><LogOut size={20}/></button>
                </div>
            ) : (
                <Link to="/login" onClick={onClose} className="block w-full bg-slate-900 text-white font-bold py-4 rounded-2xl text-center shadow-xl active:scale-95 transition">Accedi o Registrati</Link>
            )}
            {isAdmin && (<Link to="/admin" onClick={onClose} className="flex items-center justify-center gap-2 w-full bg-red-50 text-red-600 font-bold py-4 rounded-2xl border border-red-100 mt-4"><Shield size={20}/> Pannello Admin</Link>)}
        </div>
    </div>
  );
};

// --- FOOTER ---
const Footer = () => (
    <footer className="bg-white text-slate-500 py-16 border-t border-slate-100 mt-auto relative overflow-hidden mb-20 md:mb-0 w-full">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-4 gap-12 mb-12 relative z-10">
            <div className="col-span-2 space-y-6">
                <div className="flex items-center gap-2">
                    <div className="bg-slate-900 p-1.5 rounded-lg"><BookOpen className="h-4 w-4 text-white" /></div>
                    <span className="font-bold text-xl tracking-wide text-slate-900">Tuscia<span className="text-indigo-600">Hub</span></span>
                </div>
                <p className="text-sm leading-relaxed text-slate-500 max-w-sm font-medium">La piattaforma indipendente fatta dagli studenti per gli studenti</p>
                <div className="flex gap-4">
                    {[Instagram, Linkedin, Mail].map((IconName, i) => (<div key={i} className="w-10 h-10 rounded-full bg-slate-50 border border-slate-100 flex items-center justify-center hover:bg-slate-900 hover:text-white transition-all cursor-pointer"><IconName size={18} /></div>))}
                </div>
            </div>
            <div>
                <h4 className="font-bold mb-6 uppercase text-xs tracking-widest text-slate-400">Esplora</h4>
                <ul className="space-y-3 text-sm">
                    {[{ label: 'Corsi di Laurea', path: '/dipartimenti' }, { label: 'Mercatino Libri', path: '/mercatino' }, { label: 'Cerca Casa', path: '/affitti' }, { label: 'Offerte di Lavoro', path: '/jobs' }, { label: 'Eventi e Nightlife', path: '/eventi' }, { label: 'Partner e Sconti', path: '/partner' }].map((item, i) => (<li key={i}><Link to={item.path} className="text-slate-600 hover:text-indigo-600 transition-colors flex items-center gap-2 font-medium"><ChevronRight size={14} className="text-slate-300"/> {item.label}</Link></li>))}
                </ul>
            </div>
            <div>
                <h4 className="font-bold mb-6 uppercase text-xs tracking-widest text-slate-400">Link Utili</h4>
                <ul className="space-y-3 text-sm">
                    <li><Link to="/contact" className="text-slate-600 hover:text-indigo-600 transition-colors flex items-center gap-2 font-medium"><ChevronRight size={14} className="text-slate-300"/> Contattaci</Link></li>
                    <li><Link to="/terms" className="text-slate-600 hover:text-indigo-600 transition-colors flex items-center gap-2 font-medium"><ChevronRight size={14} className="text-slate-300"/> Termini e Condizioni</Link></li>
                    <li><Link to="/privacy" className="text-slate-600 hover:text-indigo-600 transition-colors flex items-center gap-2 font-medium"><ChevronRight size={14} className="text-slate-300"/> Privacy Policy</Link></li>
                </ul>
            </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 pt-8 border-t border-slate-100 text-center text-xs text-slate-400 font-medium relative z-10">© 2025 TusciaHub. Progetto indipendente.</div>
    </footer>
);

// --- APP MAIN ---
export default function App() {
  const location = useLocation();
  const isAuthPage = ['/login', '/forgot-password', '/update-password'].includes(location.pathname);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [user, setUser] = useState(null);
  const [username, setUsername] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();
  const ADMIN_EMAIL = 'info@tusciahub.it';

  useEffect(() => {
    const checkUser = async () => { const { data: { user } } = await supabase.auth.getUser(); updateUserState(user); };
    checkUser();
    const { data: authListener } = supabase.auth.onAuthStateChange((_, session) => { updateUserState(session?.user ?? null); });
    return () => authListener.subscription.unsubscribe();
  }, []);

  const updateUserState = (user) => {
      setUser(user);
      if (user) {
          setUsername(user.user_metadata?.username || user.email.split('@')[0]);
          setIsAdmin(user.email?.toLowerCase() === ADMIN_EMAIL);
      } else { setUsername(''); setIsAdmin(false); }
  };

  const handleLogout = async () => { await supabase.auth.signOut(); setUser(null); setUsername(''); setIsAdmin(false); navigate('/'); };

  return (
    <div className="font-sans text-slate-800 bg-slate-50 min-h-screen flex flex-col w-full overflow-x-hidden relative selection:bg-indigo-100 selection:text-indigo-900 pb-20 md:pb-0"> 
      <AutoLogout />
      {!isAuthPage && <Navbar user={user} username={username} isAdmin={isAdmin} handleLogout={handleLogout} />} 
      
      <div className={`flex-grow w-full ${!isAuthPage ? 'pt-20' : ''}`}> 
        <Routes>
           {/* PAGINE PUBBLICHE (NON PROTETTE) */}
           <Route path="/" element={<Home />} />
           <Route path="/login" element={<Auth />} />
           <Route path="/forgot-password" element={<ForgotPassword />} />
           <Route path="/update-password" element={<UpdatePassword />} />
           <Route path="/contact" element={<Contact />} />
           <Route path="/terms" element={<TermsAndConditions />} />
           <Route path="/privacy" element={<PrivacyPolicy />} />
           
           {/* PAGINE PROTETTE DA STUDENTROUTE (Gestione Ruoli & Blocco Aziende non Approvate) */}
           
           <Route path="/profile" element={<StudentRoute><Profile /></StudentRoute>} />
           
           {/* DIDATTICA (Bloccata per Aziende) */}
           <Route path="/dipartimenti" element={<StudentRoute><Departments /></StudentRoute>} />
           <Route path="/dept/:deptId" element={<StudentRoute><DepartmentDetails /></StudentRoute>} />
           <Route path="/course/:deptId/:courseId" element={<StudentRoute><CourseDetails /></StudentRoute>} />
           <Route path="/subject/:deptId/:courseId/:subjectName" element={<StudentRoute><Subject /></StudentRoute>} />
           <Route path="/note/:noteId" element={<StudentRoute><NoteDetails /></StudentRoute>} />

           {/* SERVIZI (Gestiti da StudentRoute per permessi specifici) */}
           <Route path="/partner" element={<StudentRoute><Partners /></StudentRoute>} />
           <Route path="/jobs" element={<StudentRoute><Jobs /></StudentRoute>} />
           <Route path="/eventi" element={<StudentRoute><Events /></StudentRoute>} />
           <Route path="/merch" element={<StudentRoute><Merch /></StudentRoute>} />
           <Route path="/affitti" element={<StudentRoute><Housing /></StudentRoute>} />
           <Route path="/ripetizioni" element={<StudentRoute><Tutoring /></StudentRoute>} />
           <Route path="/mercatino" element={<StudentRoute><Marketplace /></StudentRoute>} />
           
           {/* ADMIN (Solo Admin) */}
           <Route path="/admin" element={<StudentRoute><AdminDashboard /></StudentRoute>} />
        </Routes>
      </div>

      {!isAuthPage && <Footer />}
      {!isAuthPage && <MobileBottomNav user={user} onToggleMenu={() => setMobileMenuOpen(!mobileMenuOpen)} isMenuOpen={mobileMenuOpen} />}
      <MobileMenuOverlay isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} user={user} username={username} isAdmin={isAdmin} handleLogout={handleLogout} />
      
      <CookieBanner />
      <ScrollToTop />
      {localStorage.getItem('tusciahub_cookie_consent') === 'accepted' && <Analytics />}
    </div>
  );
}